import {
  Box,
  IconButton,
  ListItemText,
  MenuItem,
  Select,
  type SelectChangeEvent,
  Typography,
} from "@mui/material";
import { CloseIcon, ExpandArrowIcon } from "Components/Shared/Icons";
import { TransactionDirection } from "Infrastructure/Api/Api";
import type { ReactNode } from "react";
import { Resources, useResource } from "Translations/Resources";

const PageResources = Resources.Components.Transactions.Filters;
const DirectionResources = Resources.Transactions.TransactionDirection;

const PADDING_RIGHT_WITH_CLEAR = 11;
const PADDING_RIGHT_DEFAULT = 8;

type Props = {
  value?: TransactionDirection;
  onChange: (value: TransactionDirection | undefined) => void;
};

const DropdownIcon = () => (
  <Box
    sx={({ palette }) => ({
      position: "absolute",
      right: 12,
      pointerEvents: "none",
      display: "flex",
      alignItems: "center",
      top: "50%",
      transform: "translateY(-50%)",
      "& svg": {
        width: 16,
        height: 16,
        color: palette.text.secondary,
        opacity: 0.7,
      },
    })}
  >
    <ExpandArrowIcon />
  </Box>
);

const DIRECTION_OPTIONS = [
  TransactionDirection.Incoming,
  TransactionDirection.Outgoing,
];

const renderValue = (
  selected: TransactionDirection | "",
  t: (key: string) => string,
): ReactNode => {
  const styles = {
    color: "text.primary",
    fontSize: 16,
    fontWeight: 500,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    marginRight: "auto",
  };

  if (selected === "") {
    return (
      <Typography {...styles}>{t(PageResources.AllDirections)}</Typography>
    );
  }

  return <Typography {...styles}>{t(DirectionResources[selected])}</Typography>;
};

export const DirectionFilterDropdown = ({ value, onChange }: Props) => {
  const { t } = useResource();

  const handleChange = (
    event: SelectChangeEvent<TransactionDirection | "">,
  ) => {
    const newValue = event.target.value;
    onChange(newValue === "" ? undefined : (newValue as TransactionDirection));
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(undefined);
  };

  const hasValue = value !== undefined && value !== null;

  return (
    <Box position="relative" display="inline-block" width="100%">
      <Select<TransactionDirection | "">
        value={value || ""}
        onChange={handleChange}
        displayEmpty
        variant="outlined"
        IconComponent={DropdownIcon}
        inputProps={{ "aria-label": t(PageResources.Direction) }}
        renderValue={(selected) => renderValue(selected, t)}
        sx={(theme) => ({
          width: "100%",
          backgroundColor: "grey.300",
          borderRadius: "20px",
          boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
          height: 40,
          "&:before, &:after": {
            content: "none",
          },
          "& .MuiSelect-select": {
            padding: `${theme.spacing(1.5)} ${theme.spacing(3)} !important`,
            paddingRight: `${theme.spacing(hasValue ? PADDING_RIGHT_WITH_CLEAR : PADDING_RIGHT_DEFAULT)} !important`,
            minHeight: 40,
            height: 40,
            display: "flex",
            alignItems: "center",
            color: "text.primary",
            fontSize: 16,
            fontWeight: 500,
          },
          "& .MuiOutlinedInput-notchedOutline": {
            border: "none",
          },
          "&:hover": {
            backgroundColor: "grey.400",
            "& .MuiOutlinedInput-notchedOutline": {
              border: "none",
            },
          },
          "&.Mui-focused": {
            backgroundColor: "grey.400",
            "& .MuiOutlinedInput-notchedOutline": {
              border: "none",
            },
          },
        })}
      >
        <MenuItem value="">
          <ListItemText primary={t(PageResources.AllDirections)} />
        </MenuItem>
        {DIRECTION_OPTIONS.map((direction) => (
          <MenuItem
            key={direction}
            value={direction}
            sx={{
              "& .MuiTouchRipple-root": {
                color: "primary.main",
              },
            }}
          >
            <ListItemText primary={t(DirectionResources[direction])} />
          </MenuItem>
        ))}
      </Select>
      {hasValue && <ClearButton onClick={handleClear} />}
    </Box>
  );
};

type ClearButtonProps = {
  onClick: (e: React.MouseEvent) => void;
};

const ClearButton = ({ onClick }: ClearButtonProps) => (
  <IconButton
    onClick={onClick}
    aria-label="Clear filter"
    size="small"
    sx={{
      position: "absolute",
      right: 36,
      top: "50%",
      transform: "translateY(-50%)",
      padding: 0.5,
      zIndex: 1,
      "& svg": {
        width: 14,
        height: 14,
        color: "text.primary",
        opacity: 0.7,
      },
      "&:hover": {
        backgroundColor: "transparent",
        "& svg": {
          opacity: 1,
        },
      },
    }}
  >
    <CloseIcon />
  </IconButton>
);
